import React, { useMemo, useState } from 'react';
import { Check } from 'lucide-react';

const FRIEDRICH_MEDIA_BASE = 'https://shop.friedrich-motorsport.de';

function resolveImg(src) {
  if (!src) return '';
  return src.startsWith('http') ? src : FRIEDRICH_MEDIA_BASE + src;
}

function fmtEur(v) {
  if (v == null) return '';
  return Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

/**
 * Visual Multi-Dim Konfigurator fuer Friedrich Motorsport Duplex-Anlagen.
 *
 * Ersetzt die 3 nativen PDP-Dropdowns durch eine reichhaltige visuelle UI:
 *   - Material: 2 grosse Kacheln (Carbon / Edelstahl) - Filter fuer Endrohre
 *   - Endrohr:  Bild-Grid mit Thumbnails, gefiltert nach Material, klickbar
 *   - Schuerze: Radio-Buttons (nur sichtbar wenn > 1 Option)
 *
 * State: liest+schreibt `selectedAttrs` (Parent-State) via onSelect Callback.
 * Kompatibel zu PDP-`matchedVariant` und `addToCart`-Flow.
 */
export default function FriedrichVisualPicker({ product, selectedAttrs, onSelect, matchedVariant }) {
  const [endrohrLimit, setEndrohrLimit] = useState(24);
  const variants = product?.variants || [];

  // Extract axes from variants
  const { materials, endrohreByMat, bumperOpts, endrohrByKey } = useMemo(() => {
    const matSet = new Set();
    const bumperSet = new Set();
    const byMat = {};
    const byKey = {};

    variants.forEach(v => {
      const opts = v.variation_options || [];
      const mat = opts.find(o => o.attribute === 'Material')?.value || '';
      const endr = opts.find(o => o.attribute === 'Endrohr')?.value || '';
      const bumper = opts.find(o => o.attribute === 'Schürze')?.value || '';
      if (mat) matSet.add(mat);
      if (bumper) bumperSet.add(bumper);
      if (mat && endr) {
        if (!byMat[mat]) byMat[mat] = new Map();
        // dedupe by endrohr value
        if (!byMat[mat].has(endr)) {
          byMat[mat].set(endr, {
            value: endr,
            sub_sku: v.variant_tailpipe_sub_sku || '',
            image_url: v.variant_tailpipe_image_url || '',
            surcharge: Number(v.variant_tailpipe_surcharge || 0),
          });
        }
      }
      const k = `${mat}||${endr}||${bumper}`;
      if (!byKey[k]) byKey[k] = v;
    });

    const materials = Array.from(matSet).sort((a, b) => {
      // Edelstahl first (usually cheaper/default), then Carbon
      if (a === 'Edelstahl') return -1;
      if (b === 'Edelstahl') return 1;
      return a.localeCompare(b);
    });
    const endrohreByMat = {};
    Object.entries(byMat).forEach(([m, map]) => {
      endrohreByMat[m] = Array.from(map.values());
    });
    return {
      materials,
      endrohreByMat,
      bumperOpts: Array.from(bumperSet).sort(),
      endrohrByKey: byKey,
    };
  }, [variants]);

  const selectedMat = selectedAttrs?.['Material'] || '';
  const selectedEndrohr = selectedAttrs?.['Endrohr'] || '';
  const selectedBumper = selectedAttrs?.['Schürze'] || '';

  // Auto-preselect Schuerze if only 1 option
  React.useEffect(() => {
    if (bumperOpts.length === 1 && selectedBumper !== bumperOpts[0]) {
      onSelect('Schürze', bumperOpts[0]);
    }
  }, [bumperOpts, selectedBumper, onSelect]);

  const visibleEndrohre = selectedMat ? (endrohreByMat[selectedMat] || []) : [];

  const handleMat = (mat) => {
    onSelect('Material', mat);
    // Reset endrohr if switching material (endrohr belongs to a material)
    if (selectedEndrohr) onSelect('Endrohr', '');
  };
  const handleEndrohr = (endr) => onSelect('Endrohr', endr.value);
  const handleBumper = (b) => onSelect('Schürze', b);

  return (
    <div className="fried-picker" data-testid="friedrich-visual-picker">
      {/* Material Selection */}
      <div className="fried-picker-section" data-testid="fried-picker-material">
        <h4 className="fried-picker-heading">1. Material wählen</h4>
        <div className="fried-mat-grid">
          {materials.map(mat => {
            const isActive = selectedMat === mat;
            const isCarbon = mat.toLowerCase().includes('carbon');
            return (
              <button
                key={mat}
                type="button"
                data-testid={`fried-mat-${mat}`}
                className={`fried-mat-tile${isActive ? ' is-active' : ''}`}
                onClick={() => handleMat(mat)}
                aria-pressed={isActive}
              >
                <div className={`fried-mat-swatch fried-mat-swatch--${isCarbon ? 'carbon' : 'edelstahl'}`}>
                  {isActive && <Check size={20} className="fried-mat-check" strokeWidth={3} />}
                </div>
                <div className="fried-mat-label">{mat}</div>
                {isCarbon && <div className="fried-mat-hint">+ Aufpreis</div>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Endrohr Selection (visual grid) */}
      {selectedMat && (
        <div className="fried-picker-section" data-testid="fried-picker-endrohr">
          <h4 className="fried-picker-heading">
            2. Endrohr wählen <span className="fried-picker-count">({visibleEndrohre.length} Varianten)</span>
          </h4>
          <div className="fried-endrohr-grid" data-testid="fried-endrohr-grid">
            {visibleEndrohre.slice(0, endrohrLimit).map(e => {
              const isActive = selectedEndrohr === e.value;
              return (
                <button
                  key={e.sub_sku || e.value}
                  type="button"
                  data-testid={`fried-endrohr-${e.sub_sku || e.value}`}
                  className={`fried-endrohr-tile${isActive ? ' is-active' : ''}`}
                  onClick={() => handleEndrohr(e)}
                  aria-pressed={isActive}
                  title={e.value}
                >
                  <div className="fried-endrohr-thumb">
                    {e.image_url ? (
                      <img src={resolveImg(e.image_url)} alt={e.value} loading="lazy" />
                    ) : (
                      <div className="fried-endrohr-thumb-fallback">
                        {e.sub_sku || 'Endrohr'}
                      </div>
                    )}
                    {isActive && <div className="fried-endrohr-check"><Check size={16} strokeWidth={3} /></div>}
                  </div>
                  <div className="fried-endrohr-label">{e.value}</div>
                  {e.surcharge > 0 && (
                    <div className="fried-endrohr-surcharge">
                      + {fmtEur(2 * e.surcharge)}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
          {visibleEndrohre.length > endrohrLimit && (
            <button
              type="button"
              className="fried-picker-more"
              data-testid="fried-endrohr-more"
              onClick={() => setEndrohrLimit(v => v + 24)}
            >
              {visibleEndrohre.length - endrohrLimit} weitere anzeigen
            </button>
          )}
        </div>
      )}

      {/* Schuerze */}
      {bumperOpts.length > 1 && selectedEndrohr && (
        <div className="fried-picker-section" data-testid="fried-picker-bumper">
          <h4 className="fried-picker-heading">3. Schürze wählen</h4>
          <div className="fried-bumper-row">
            {bumperOpts.map(b => {
              const isActive = selectedBumper === b;
              return (
                <button
                  key={b}
                  type="button"
                  data-testid={`fried-bumper-${b}`}
                  className={`fried-bumper-tile${isActive ? ' is-active' : ''}`}
                  onClick={() => handleBumper(b)}
                  aria-pressed={isActive}
                >
                  {b}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Selected Summary */}
      {selectedMat && selectedEndrohr && selectedBumper && matchedVariant && (
        <div className="fried-picker-summary" data-testid="fried-picker-summary">
          <div className="fried-picker-summary-row">
            <span>Deine Konfiguration:</span>
            <strong>{selectedMat} · {selectedEndrohr} · {selectedBumper}</strong>
          </div>
          <div className="fried-picker-summary-price">
            {fmtEur(matchedVariant.price)}
          </div>
        </div>
      )}
    </div>
  );
}
